import { useCallback } from "react"
import { Capacitor, CapacitorHttp, type HttpHeaders, type HttpOptions } from "@capacitor/core"
import { toast } from "sonner"
import { useAuth } from '@/modules/shared/components/auth-provider'
import { URL, USR } from '@/modules/shared/lib/consts'
import { safeJsonParse } from '@/modules/shared/lib/utils'
import { appendAppVersionHeaders } from '@/modules/shared/lib/app-version'

// ============================================================================
// 🛡️ PROTEÇÃO DE CONCORRÊNCIA / RACE CONDITION
// Contadores GLOBAIS atômicos (fora do React hook = mesmo para TODAS as telas/hooks)
// ============================================================================
const globalPendingRequests: { value: number } = { value: 0 };
const globalLastIdentityChangedAtMs: { value: number } = { value: 0 };

export const isAnyRequestPendingGlobal = (): boolean => globalPendingRequests.value > 0;
export const pendingRequestsCountGlobal = (): number => globalPendingRequests.value;
export const markIdentityChangedGlobal = (): void => {
  globalLastIdentityChangedAtMs.value = Date.now();
};
export const didIdentityChangeRecentlyGlobal = (withinMs = 3000): boolean =>
  (Date.now() - globalLastIdentityChangedAtMs.value) < withinMs;

// ============================================================================
// Fim proteção
// ============================================================================

const TEMPO_LIMITE_PADRAO_MS = 60_000
const TEMPO_LIMITE_IA_MS = 300_000
const STATUS_SERVIDOR_INDISPONIVEL = new Set([502, 503, 504])

const paginaDescarregando: { value: boolean } = { value: false }
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => { paginaDescarregando.value = true })
  window.addEventListener('beforeunload', () => { paginaDescarregando.value = true })
  window.addEventListener('pageshow', () => { paginaDescarregando.value = false })
}

const tempoLimiteDe = (resource: string): number =>
  resource.startsWith('/AI') ? TEMPO_LIMITE_IA_MS : TEMPO_LIMITE_PADRAO_MS

const _send = async (options: HttpOptions, tempoLimiteMs: number) => {
  if (Capacitor.isNativePlatform()) {
    return await CapacitorHttp.request({ ...options, connectTimeout: 15_000, readTimeout: tempoLimiteMs })
  }
  const controlador = new AbortController()
  let tempoEsgotado = false
  const temporizador = setTimeout(() => {
    tempoEsgotado = true
    controlador.abort()
  }, tempoLimiteMs)
  try {
    const response = await fetch(options.url, {
      method: options.method,
      headers: options.headers,
      body: options.data,
      signal: controlador.signal
    })
    if (response.status === 401) return { status: 401, data: null }
    try {
      return { status: response.status, data: await response.json() }
    } catch {
      return { status: response.status, data: null }
    }
  } catch (err) {
    const mensagem = String((err as any)?.message ?? '').toLowerCase()
    const ehAbortoDoNavegador = err instanceof DOMException && err.name === 'AbortError'
    const ehFalhaDeRede = err instanceof TypeError && (/failed to fetch/.test(mensagem) || /networkerror/.test(mensagem))
    if (tempoEsgotado) return { status: 0, data: null, aborted: true, tempoEsgotado: true }
    if (ehAbortoDoNavegador || ehFalhaDeRede) {
      return { status: 0, data: null, aborted: true, semConexao: ehFalhaDeRede }
    }
    throw err
  } finally {
    clearTimeout(temporizador)
  }
}

const ADMIN_PROCS_SKIP_X_SCHOOL_ID = new Set([
  'obter_escolas',
  'obter_escolas_select',
  'persistir_escola',
  'obter_admins',
  'persistir_admin',
  'obter_auditoria',
  'obter_emails_escola'
])

type RequestOptions = {
  skipSuperSchoolId?: boolean
  identityOverride?: Identity
}

const useRequest = () => {
  const { logout } = useAuth();

  const request = useCallback(async (resource: string, method?: 'GET' | 'POST', body?: unknown, options?: RequestOptions): Promise<IResponse> => {
    // 🛡️ Contador global: INCREMENTA antes de tudo
    globalPendingRequests.value++;
    // Identidade recém-obtida (login/impersonate) que ainda não está no storage e acabou de
    // acionar markIdentityChangedGlobal: usa o token dela e ignora a janela de descarte de 3 s.
    const identidadeInformada = options?.identityOverride
    try {
      try {
        // 🛡️ Early exit: checagem troca de identidade RECENTE (< 3s) FEITA ANTES do fetch.
        // Sem isso, o fetch acontecia, era retornado do servidor, e nós o descartávamos
        // no pós-fetch (checagem abaixo). Agora economizamos rede e evitamos ruído em dev.
        // Caso esteja dentro da janela, retornamos exatamente o mesmo resultado do pós-fetch
        // (success=false + err) para manter compatibilidade e o caller retentar.
        if (!identidadeInformada && didIdentityChangeRecentlyGlobal(3000)) {
          return {
            success: false,
            err: new Error('Request descartado após troca de identidade')
          }
        }

        const usr = identidadeInformada ? JSON.stringify(identidadeInformada) : localStorage.getItem(USR)
        if (!usr) {
          logout?.()
          return { success: false, err: new Error('Não autorizado') }
        }

        const parsedUsr = safeJsonParse<any>(usr)
        const token = parsedUsr?.token
        const headers: HttpHeaders = {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }

        const userRole = parsedUsr?.role
        if (userRole && String(userRole).trim() !== '') {
          headers['X-User-Role'] = String(userRole)
        }

        const skipViaOptions = options?.skipSuperSchoolId === true
        const matchProc = resource.match(/^\/proc\/([a-zA-Z0-9_]+)/)
        const procName = matchProc?.[1]?.toLowerCase()
        const skipViaAdminProc = !!procName && ADMIN_PROCS_SKIP_X_SCHOOL_ID.has(procName)
        const skipViaAdminPath = resource.startsWith('/admin/')

        const schoolId = parsedUsr?.school_id
        const isSuper = parsedUsr?.role === 'SUPER'
        if (!skipViaOptions && !skipViaAdminProc && !skipViaAdminPath && isSuper && schoolId !== undefined && schoolId !== null && String(schoolId).trim() !== '') {
          headers['X-School-Id'] = String(schoolId)
        }

        const headersComVersao = appendAppVersionHeaders(headers);

        const response = await _send({
          url: `${URL}${resource}`,
          method: method ?? 'GET',
          headers: headersComVersao,
          data: JSON.stringify(body) ?? null
        }, tempoLimiteDe(resource))

        const resumoAny = response as any
        const foiAbortado = Boolean(resumoAny?.aborted)
        // 🛡️ Segunda checagem (após fetch): cobre casos em que troca de identidade
        // aconteceu ENQUANTO esperava a resposta.
        const houveTrocaDeIdentidadeRecente = !identidadeInformada && didIdentityChangeRecentlyGlobal(3000)

        if (response.status === 401) {
          toast.error('Sessão expirada. Faça login novamente.')
          logout?.()
          return { success: false, err: new Error('Não autorizado') }
        }

        if (!houveTrocaDeIdentidadeRecente && resumoAny?.tempoEsgotado) {
          toast.error('O servidor demorou para responder. Tente novamente.', { id: 'servidor-demorou' })
        } else if (!houveTrocaDeIdentidadeRecente && resumoAny?.semConexao && !paginaDescarregando.value) {
          toast.error('Sem conexão com o servidor. Verifique sua internet e tente novamente.', { id: 'sem-conexao' })
        }

        if (!foiAbortado && !houveTrocaDeIdentidadeRecente && STATUS_SERVIDOR_INDISPONIVEL.has(response.status)) {
          toast.error('O servidor está indisponível no momento. Tente novamente em instantes.', { id: 'servidor-indisponivel' })
          return { success: false, err: new Error('Servidor indisponível') }
        }

        if (foiAbortado || houveTrocaDeIdentidadeRecente) {
          return {
            success: false,
            err: new Error(foiAbortado ? 'Request abortado' : 'Request descartado após troca de identidade')
          }
        }

        return { success: true, data: response.data }
      } catch (err) {
        const abortOuTrocaRecente =
          (err instanceof DOMException && err.name === 'AbortError') ||
          (!identidadeInformada && didIdentityChangeRecentlyGlobal(3000))
        if (abortOuTrocaRecente) {
          return { success: false, err }
        }
        toast.error('Houve uma falha na comunicação com o servidor.')
        return { success: false, err }
      }
    } finally {
      // 🛡️ Contador global: DECREMENTA SEMPRE (mesmo return/throw/erro/exception)
      if (globalPendingRequests.value > 0) globalPendingRequests.value--;
    }
  }, [logout]);

  const get = useCallback(async (resource: string, options?: RequestOptions) => request(resource, 'GET', undefined, options), [request])
  const post = useCallback(async (resource: string, body: object, options?: RequestOptions) => request(resource, 'POST', body, options), [request])

  return { get, post }
};

export default useRequest
